import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Shield, Search, Download, RefreshCw, AlertTriangle, CheckCircle,
  XCircle, User, Clock, Monitor, Globe, Filter, Eye, Lock, Unlock,
  Activity, FileText, ChevronLeft, ChevronRight, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Action type categories with Italian labels and colors
const ACTION_CATEGORIES = {
  'auth': { label: 'Autenticazione', color: 'bg-blue-100 text-blue-800', icon: Lock },
  'user': { label: 'Utenti', color: 'bg-green-100 text-green-800', icon: User },
  'document': { label: 'Documenti', color: 'bg-yellow-100 text-yellow-800', icon: FileText },
  'property': { label: 'Immobili', color: 'bg-purple-100 text-purple-800', icon: Activity },
  'gara': { label: 'Gare di Vendita', color: 'bg-orange-100 text-orange-800', icon: Activity },
  'payment': { label: 'Pagamenti', color: 'bg-emerald-100 text-emerald-800', icon: Activity },
  'admin': { label: 'Admin', color: 'bg-red-100 text-red-800', icon: Shield },
  'gdpr': { label: 'GDPR', color: 'bg-pink-100 text-pink-800', icon: Shield },
  'security': { label: 'Sicurezza', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  'chat': { label: 'Chat', color: 'bg-cyan-100 text-cyan-800', icon: Activity },
  'mandate': { label: 'Mandati', color: 'bg-indigo-100 text-indigo-800', icon: FileText },
};

const getActionCategory = (actionType) => {
  const category = actionType?.split('.')[0] || 'unknown';
  return ACTION_CATEGORIES[category] || { label: 'Altro', color: 'bg-gray-100 text-gray-800', icon: Activity };
};

const AuditLogRow = ({ log, onClick }) => {
  const category = getActionCategory(log.action_type);
  const CategoryIcon = category.icon;
  
  return (
    <tr 
      className="hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => onClick(log)}
      data-testid={`audit-log-row-${log.log_id}`}
    >
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center space-x-2">
          <div className={`p-1.5 rounded-lg ${category.color}`}>
            <CategoryIcon className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              {log.action_type?.split('.').pop()?.replace(/_/g, ' ').toUpperCase()}
            </p>
            <p className="text-xs text-gray-500">{category.label}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center space-x-2">
          <User className="h-4 w-4 text-gray-400" />
          <div>
            <p className="text-sm text-gray-900">{log.user_id || 'Anonimo'}</p>
            <p className="text-xs text-gray-500 capitalize">{log.user_type || '-'}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 max-w-xs truncate text-sm text-gray-600">
        {log.description || '-'}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center space-x-1 text-xs text-gray-500">
          <Globe className="h-3 w-3" />
          <span>{log.ip_address || '-'}</span>
        </div>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        {log.success ? (
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            OK
          </Badge>
        ) : (
          <Badge className="bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Errore
          </Badge>
        )}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
        {log.timestamp ? format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: it }) : '-'}
      </td>
    </tr>
  );
};

const LogDetailDialog = ({ log, isOpen, onClose }) => {
  if (!log) return null;
  
  const category = getActionCategory(log.action_type);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <span>Dettaglio Log Audit</span>
          </DialogTitle>
          <DialogDescription>
            Log ID: {log.log_id}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Azione</Label>
              <div className="flex items-center space-x-2">
                <Badge className={category.color}>{category.label}</Badge>
                <span className="text-sm font-medium">{log.action_type}</span>
              </div>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Stato</Label>
              <Badge className={log.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                {log.success ? 'Successo' : 'Errore'}
              </Badge>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Utente</Label>
              <p className="text-sm">{log.user_id || 'Anonimo'}</p>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Tipo Utente</Label>
              <p className="text-sm capitalize">{log.user_type || '-'}</p>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">IP Address</Label>
              <p className="text-sm font-mono">{log.ip_address || '-'}</p>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Timestamp</Label>
              <p className="text-sm">
                {log.timestamp ? format(new Date(log.timestamp), 'dd MMMM yyyy, HH:mm:ss', { locale: it }) : '-'}
              </p>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Entità</Label>
              <p className="text-sm">{log.entity_type || '-'} {log.entity_id ? `(${log.entity_id})` : ''}</p>
            </div>
            
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Livello Sensibilità</Label>
              <Badge variant="outline">{log.sensitivity_level || '-'}</Badge>
            </div>
          </div>
          
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Descrizione</Label>
            <p className="text-sm bg-gray-50 p-3 rounded-lg">{log.description || '-'}</p>
          </div>
          
          {log.user_agent && (
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">User Agent</Label>
              <p className="text-xs font-mono bg-gray-50 p-2 rounded overflow-x-auto">{log.user_agent}</p>
            </div>
          )}
          
          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Metadata</Label>
              <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto">
                {JSON.stringify(log.metadata, null, 2)}
              </pre>
            </div>
          )}
          
          {log.error_message && (
            <div className="space-y-1">
              <Label className="text-xs text-gray-500 text-red-600">Messaggio Errore</Label>
              <p className="text-sm bg-red-50 text-red-800 p-3 rounded-lg">{log.error_message}</p>
            </div>
          )}
          
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Checksum Integrità</Label>
            <p className="text-xs font-mono text-gray-400">{log.checksum}</p>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SecurityAlertsPanel = ({ token }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const res = await axios.get(`${API}/api/admin/security-alerts?hours=24`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAlerts(res.data.alerts || []);
      } catch (err) {
        console.error('Error fetching alerts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAlerts();
  }, [token]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (alerts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <CheckCircle className="h-12 w-12 mx-auto text-green-400 mb-2" />
        <p>Nessun alert di sicurezza nelle ultime 24 ore</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      {alerts.slice(0, 5).map((alert, idx) => (
        <div key={idx} className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg border border-red-200">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-red-800">{alert.action_type}</p>
            <p className="text-xs text-red-600 truncate">{alert.description}</p>
          </div>
          <span className="text-xs text-red-500">
            {format(new Date(alert.timestamp), 'HH:mm', { locale: it })}
          </span>
        </div>
      ))}
    </div>
  );
};

const BlockedIPsPanel = ({ token }) => {
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const fetchBlockedIPs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/blocked-ips`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedIPs(res.data.blocked_ips || []);
    } catch (err) {
      console.error('Error fetching blocked IPs:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    fetchBlockedIPs();
  }, [fetchBlockedIPs]);
  
  const handleUnblock = async (ip) => {
    try {
      await axios.delete(`${API}/api/admin/block-ip/${ip}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`IP ${ip} sbloccato`);
      fetchBlockedIPs();
    } catch (err) {
      toast.error('Errore nello sblocco IP');
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (blockedIPs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Unlock className="h-12 w-12 mx-auto text-green-400 mb-2" />
        <p>Nessun IP bloccato</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-2">
      {blockedIPs.map((item, idx) => (
        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-3">
            <Lock className="h-4 w-4 text-red-500" />
            <div>
              <p className="text-sm font-mono">{item.ip}</p>
              <p className="text-xs text-gray-500">
                Scade: {format(new Date(item.expires_at), 'dd/MM HH:mm', { locale: it })}
              </p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => handleUnblock(item.ip)}
            className="text-blue-600 hover:text-blue-800"
          >
            <Unlock className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
};

export default function AuditLogsPage({ token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const LIMIT = 25;
  
  // Filters
  const [filters, setFilters] = useState({
    user_type: '',
    action_type: '',
    success_only: '',
    start_date: '',
    end_date: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: String(page * LIMIT),
        limit: String(LIMIT)
      });
      
      if (filters.user_type) params.append('user_type', filters.user_type);
      if (filters.action_type) params.append('action_type', filters.action_type);
      if (filters.success_only) params.append('success_only', filters.success_only === 'true');
      if (filters.start_date) params.append('start_date', new Date(filters.start_date).toISOString());
      if (filters.end_date) params.append('end_date', new Date(filters.end_date).toISOString());
      
      const res = await axios.get(`${API}/api/admin/audit-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLogs(res.data.logs || []);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      toast.error('Errore nel caricamento dei log');
    } finally {
      setLoading(false);
    }
  }, [token, page, filters]);
  
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  
  const handleLogClick = (log) => {
    setSelectedLog(log);
    setShowDetail(true);
  };
  
  const handleExport = async () => {
    try {
      const startDate = filters.start_date || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const endDate = filters.end_date || new Date().toISOString();
      
      const res = await axios.get(
        `${API}/api/admin/audit-logs/export?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${format(new Date(), 'yyyyMMdd_HHmmss')}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`${res.data.total_records} log esportati`);
    } catch (err) {
      toast.error('Errore nell\'esportazione');
    }
  };
  
  const resetFilters = () => {
    setFilters({
      user_type: '',
      action_type: '',
      success_only: '',
      start_date: '',
      end_date: ''
    });
    setPage(0);
  };
  
  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center space-x-2">
            <Shield className="h-7 w-7 text-blue-600" />
            <span>Audit Logs</span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Monitoraggio completo delle attività di sistema
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline" 
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filtri
          </Button>
          <Button variant="outline" onClick={fetchLogs}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
          <Button onClick={handleExport} data-testid="export-logs-btn">
            <Download className="h-4 w-4 mr-2" />
            Esporta
          </Button>
        </div>
      </div>
      
      {/* Security Panels */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <span>Alert di Sicurezza (24h)</span>
          </h3>
          <SecurityAlertsPanel token={token} />
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center space-x-2">
            <Lock className="h-5 w-5 text-orange-500" />
            <span>IP Bloccati</span>
          </h3>
          <BlockedIPsPanel token={token} />
        </div>
      </div>
      
      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="grid md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">Tipo Utente</Label>
              <Select 
                value={filters.user_type} 
                onValueChange={(v) => setFilters(f => ({...f, user_type: v}))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Tutti</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="agent">Agente</SelectItem>
                  <SelectItem value="seller">Venditore</SelectItem>
                  <SelectItem value="buyer">Acquirente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs">Categoria</Label>
              <Select 
                value={filters.action_type} 
                onValueChange={(v) => setFilters(f => ({...f, action_type: v}))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tutte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Tutte</SelectItem>
                  <SelectItem value="auth.login">Login</SelectItem>
                  <SelectItem value="auth.login_failed">Login Fallito</SelectItem>
                  <SelectItem value="user.created">Utente Creato</SelectItem>
                  <SelectItem value="document.uploaded">Doc Caricato</SelectItem>
                  <SelectItem value="gara.bid_placed">Offerta Gara</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs">Stato</Label>
              <Select 
                value={filters.success_only} 
                onValueChange={(v) => setFilters(f => ({...f, success_only: v}))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tutti" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Tutti</SelectItem>
                  <SelectItem value="true">Solo Successo</SelectItem>
                  <SelectItem value="false">Solo Errori</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs">Da</Label>
              <Input 
                type="date" 
                value={filters.start_date}
                onChange={(e) => setFilters(f => ({...f, start_date: e.target.value}))}
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs">A</Label>
              <Input 
                type="date" 
                value={filters.end_date}
                onChange={(e) => setFilters(f => ({...f, end_date: e.target.value}))}
              />
            </div>
          </div>
          
          <div className="flex justify-end mt-4 space-x-2">
            <Button variant="ghost" onClick={resetFilters}>Reset</Button>
            <Button onClick={() => { setPage(0); fetchLogs(); }}>Applica Filtri</Button>
          </div>
        </div>
      )}
      
      {/* Logs Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Azione</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utente</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrizione</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stato</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto" />
                    <p className="text-sm text-gray-500 mt-2">Caricamento log...</p>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    <Activity className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                    <p>Nessun log trovato</p>
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <AuditLogRow key={log.log_id} log={log} onClick={handleLogClick} />
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-500">
            Mostrando {page * LIMIT + 1} - {Math.min((page + 1) * LIMIT, page * LIMIT + logs.length)} di {totalCount > 0 ? `${totalCount}+` : logs.length} risultati
          </p>
          <div className="flex items-center space-x-2">
            <Button 
              variant="outline" 
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-gray-600">Pagina {page + 1}</span>
            <Button 
              variant="outline" 
              size="sm"
              disabled={logs.length < LIMIT}
              onClick={() => setPage(p => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      
      {/* Detail Dialog */}
      <LogDetailDialog 
        log={selectedLog}
        isOpen={showDetail}
        onClose={() => setShowDetail(false)}
      />
    </div>
  );
}
